import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MicrosoftCallbackComponent } from './auth/microsoft-callback/microsoft-callback.component';
import { LinkLoginComponent } from './auth/link-login/link-login.component';
import { IsNotAuthenticated } from './is-not-authenticated';
import { LogoutComponent } from './logout/logout.component';
import { AppRoute } from './interfaces/app-route.interface';
import { IsAuthenticated } from './is-authenticated';
import { PlatformEditComponent } from './platform/platform-edit.component';
import { CompanyEditComponent } from './companies/edit/company-edit.component';
import { CompaniesComponent } from './companies/companies.component';
import { UsersOverviewComponent } from './users/users-overview/users-overview.component';
import { UserEditComponent } from './users/user-edit/user-edit.component';
import { RightsManagementComponent } from './rights-management/rights-management.component';
import { CheckpointOverviewComponent } from './checkpoints/checkpoint-overview.component';
import { CheckpointStoreComponent } from './checkpoints/store/checkpoint-store.component';
import { SeireComponent } from './seire/seire.component';
import { PlatformCheckpointsComponent } from './seire/platform-checkpoints/platform-checkpoints.component';
import { CheckpointResultOverviewComponent } from './checkpoints/checkpoint-result-overview/checkpoint-result-overview.component';
import { RegistrationComponent } from './time-registration/registration.component';
import { DailyComponent } from './daily/daily.component';
import { SubscriptionEditComponent } from './subscriptions/subscription-edit/subscription-edit.component';
import { SubscriptionsComponent } from './subscriptions/subscriptions.component';
import { FinancialComponent } from './financial/financial.component';
import { ReportComponent } from './report/report.component';
import { PdfBriefpapierComponent } from './pdf-briefpapier/pdf-briefpapier.component';
import { Routenames } from './classes/route-name.enum';
import { LeaveComponent } from './leave/leave.component';
import { LeaveEditComponent } from './leave/leave-edit/leave-edit.component';
import { WeeklyComponent } from './weekly/weekly.component';
import { ProfileComponent } from './profile/profile.component';
import { environment } from '../environments/environment';
import { VacancyPageComponent } from './werken-bij/vacancy-page.component';

// `title` is the browser tab title (PageTitleStrategy adds " - CRM26"). Pages whose title depends on loaded data
// (a platform name, the report filters) have no title here and set it themselves with browserTitle().
export const routes: AppRoute[] = [
    { path: '', redirectTo: `${Routenames.seire}`, pathMatch: 'full' },
    { path: 'platforms', redirectTo: `${Routenames.seire}`, pathMatch: 'full' },

    {
        path: 'login',
        title: 'Inloggen',
        component: LinkLoginComponent,
        canActivate: [IsNotAuthenticated],
    },
    {
        path: 'inloggen/entra',
        title: 'Inloggen',
        component: MicrosoftCallbackComponent,
    },
    { path: 'print/:date/:token', title: 'Inloggen', component: LinkLoginComponent },
    { path: `${Routenames.logout}`, title: 'Uitloggen', component: LogoutComponent },
    { path: `${Routenames.werkenBij}/:slug`, title: 'Werken Bij', component: VacancyPageComponent },

    {
        path: `${Routenames.seire}`,
        title: 'Seire',
        component: SeireComponent,
        canActivate: [IsAuthenticated],
    },
    {
        path: `${Routenames.seire}/:platformId`,
        component: PlatformCheckpointsComponent,
        canActivate: [IsAuthenticated],
    },
    {
        path: `${Routenames.seire}/:platformId/overview/:checkId`,
        component: CheckpointOverviewComponent,
        canActivate: [IsAuthenticated],
    },
    {
        path: `${Routenames.seire}/:platformId/store/:checkId`,
        title: 'Checkpoint - Seire',
        component: CheckpointStoreComponent,
        canActivate: [IsAuthenticated],
    },
    {
        path: `${Routenames.seire}/:platformId/overview/:checkId/:checkResultId`,
        component: CheckpointResultOverviewComponent,
        canActivate: [IsAuthenticated],
    },

    {
        path: `${Routenames.companies}`,
        title: 'Bedrijven',
        component: CompaniesComponent,
        canActivate: [IsAuthenticated],
    },
    {
        path: `${Routenames.companies}/:id`,
        title: 'Bedrijven',
        component: CompanyEditComponent,
        canActivate: [IsAuthenticated],
    },

    {
        path: `${Routenames.users}`,
        title: 'Gebruikers',
        component: UsersOverviewComponent,
        canActivate: [IsAuthenticated],
    },
    {
        path: `${Routenames.users}/:id`,
        title: 'Gebruikers',
        component: UserEditComponent,
        canActivate: [IsAuthenticated],
    },

    {
        path: `${Routenames.platforms}/:id`,
        title: 'Platform - Seire',
        component: PlatformEditComponent,
        canActivate: [IsAuthenticated],
    },

    {
        path: `${Routenames.rightsMatrix}`,
        title: 'Rechtenmatrix',
        component: RightsManagementComponent,
        canActivate: [IsAuthenticated],
        data: {
            // requiredFunctionality: Functionalities.RightsMatrix,
        },
    },
    {
        path: `${Routenames.registration}`,
        title: 'Urenregistratie',
        component: RegistrationComponent,
        canActivate: [IsAuthenticated],
    },

    {
        path: `${Routenames.leave}`,
        title: 'Verlof',
        component: LeaveComponent,
        canActivate: [IsAuthenticated],
    },
    {
        path: `${Routenames.leave}/:id`,
        title: 'Verlof',
        component: LeaveEditComponent,
        canActivate: [IsAuthenticated],
    },

    { path: `${Routenames.reports}`, component: ReportComponent, canActivate: [IsAuthenticated] },

    {
        path: `${Routenames.pdf}/${Routenames.underlay}`,
        title: 'Briefpapier',
        component: PdfBriefpapierComponent,
        canActivate: [IsAuthenticated],
    },

    {
        path: `${Routenames.daily}`,
        title: 'Daily',
        component: DailyComponent,
        canActivate: [IsAuthenticated],
    },

    {
        path: `${Routenames.weekly}`,
        title: 'Weekly',
        component: WeeklyComponent,
        canActivate: [IsAuthenticated],
    },

    {
        path: `${Routenames.profile}`,
        title: 'Profiel',
        component: ProfileComponent,
        canActivate: [IsAuthenticated],
    },

    {
        path: `${Routenames.subscriptions}`,
        title: 'Abonnementen',
        component: SubscriptionsComponent,
        canActivate: [IsAuthenticated],
    },
    {
        path: `${Routenames.subscriptions}/:id`,
        title: 'Abonnementen',
        component: SubscriptionEditComponent,
        canActivate: [IsAuthenticated],
    },

    {
        path: `${Routenames.financials}`,
        title: 'Financieel',
        component: FinancialComponent,
        canActivate: [IsAuthenticated],
    },

    // Overview of the shared UI components, only outside production
    ...(environment.production
        ? []
        : [
              {
                  path: 'dev/components',
                  title: 'Componenten',
                  loadComponent: () =>
                      import('./dev/components/dev-components.component').then(
                          (m) => m.DevComponentsComponent,
                      ),
              },
          ]),
];

@NgModule({
    imports: [RouterModule.forRoot(routes, {})],
    exports: [RouterModule],
    providers: [IsAuthenticated, IsNotAuthenticated],
})
export class AppRoutingModule {}
